/**
 * FunnelStageRow (KAN-737)
 *
 * One stage of the dashboard funnel block: name, a bar of three segments
 * (moved on / here now / refused) and the numbers. The bar length is the share
 * of `reached` from the largest `reached` of the funnel; segments inside are
 * proportional to their counts. "Сейчас здесь" and "отказ" open the clients
 * list; "ушли дальше" is not a link: those clients sit on different later stages.
 * Text shows "дошли N · X%" and "отказ M · Y%"; percents are of the whole cohort of
 * the period. An entry stage shows no percent at "дошли". The number of clients
 * here now lives only in the title of the "сейчас здесь" segment.
 */
import { useNavigate } from 'react-router-dom'
import type { FunnelStageStats } from '@/types/funnel'
import { formatPercent, formatReached, formatRefused, SEGMENT_COLORS } from './funnelStageFormat'

/** Clients list of the stage; without `outcome` the list shows every client on the stage. */
function clientsLink(
  funnel: string,
  stage: string,
  outcome?: 'open' | 'lost',
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams({ funnel, stage })
  Object.entries(extra ?? {}).forEach(([key, value]) => params.set(key, value))
  if (outcome) params.set('outcome', outcome)
  return `/clients?${params.toString()}`
}

const PERIOD_NOTE = 'Список клиентов открывается без учёта периода.'

const COHORT_NOTE = 'от всех вошедших в воронку за период'

const SEGMENT_BASE = 'h-full min-w-[6px] basis-0'

interface FunnelStageRowProps {
  stage: FunnelStageStats
  funnel: string
  maxReached: number
  /** KAN-744: extra filters of the clients-list links (manager card: manager=<id>). */
  clientsParams?: Record<string, string>
}

export function FunnelStageRow({ stage, funnel, maxReached, clientsParams }: FunnelStageRowProps) {
  const navigate = useNavigate()
  const isEntry = stage.entry_group !== null && stage.prev.length === 0
  const ratio = maxReached > 0 ? (stage.reached / maxReached) * 100 : 0
  const width = stage.reached > 0 ? Math.max(ratio, 2) : 0
  // Clients of the won stage sit there with outcome "won", not "open".
  // The won stage counts both open and won clients as "here": no outcome filter there.
  const hereOutcome = stage.outcome_type === 'won' ? undefined : 'open'

  const hereTitle = `Сейчас здесь: ${stage.here}, открыть клиентов. ${PERIOD_NOTE}`
  const refusedTitle = `Отказ: ${stage.refused} (вручную ${stage.refused_manual}, автоматически ${stage.refused_auto}), открыть клиентов. ${PERIOD_NOTE}`

  return (
    <div data-testid={`funnel-stage-${stage.codename}`} className="py-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-sm">
        <span className="flex items-center gap-1.5 text-gray-800">
          {stage.label}
          {isEntry && (
            <span className="rounded bg-sky-100 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-sky-700">
              вход
            </span>
          )}
        </span>
        <span className="flex flex-wrap gap-x-3 text-xs tabular-nums text-gray-500">
          <span title={isEntry ? 'Дошли до этапа входа' : `Дошли до этапа: ${formatPercent(stage.conversion)} ${COHORT_NOTE}`}>
            {formatReached(stage.reached, stage.conversion, isEntry)}
          </span>
          <span
            title={`Отказ на этапе: ${formatPercent(stage.refusal_pct)} ${COHORT_NOTE}`}
            className={stage.refused > 0 ? 'text-red-600' : undefined}
          >
            {formatRefused(stage.refused, stage.refusal_pct)}
          </span>
        </span>
      </div>
      <div className="mt-1 h-3 w-full rounded-full bg-gray-100">
        <div
          data-testid="funnel-bar"
          className="flex h-full gap-px overflow-hidden rounded-full"
          style={{ width: `${width}%` }}
        >
          {stage.moved_on > 0 && (
            <span
              data-testid="segment-moved_on"
              title={`Ушли дальше: ${stage.moved_on}`}
              className={`${SEGMENT_BASE} ${SEGMENT_COLORS.moved_on}`}
              style={{ flexGrow: stage.moved_on }}
            />
          )}
          {stage.here > 0 && (
            <button
              type="button"
              data-testid="segment-here"
              title={hereTitle}
              aria-label={`${stage.label}. ${hereTitle}`}
              onClick={() => navigate(clientsLink(funnel, stage.codename, hereOutcome, clientsParams))}
              className={`${SEGMENT_BASE} ${SEGMENT_COLORS.here} hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
              style={{ flexGrow: stage.here }}
            />
          )}
          {stage.refused > 0 && (
            <button
              type="button"
              data-testid="segment-refused"
              title={refusedTitle}
              aria-label={`${stage.label}. ${refusedTitle}`}
              onClick={() => navigate(clientsLink(funnel, stage.codename, 'lost', clientsParams))}
              className={`${SEGMENT_BASE} ${SEGMENT_COLORS.refused} hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300`}
              style={{ flexGrow: stage.refused }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
